import React, { FC, useEffect, useRef, useState } from 'react';
import { Form, Modal, Badge, Button } from 'react-bootstrap';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import styles from './Calendar.module.scss';
import RealtiCalendar from './Calendar';
import axios from 'axios';
import { lddGlobalSessionInfo } from '../navigationTypes';
import { useAppSelector } from '../../redux/hooks';
import { format } from 'date-fns';
import moment from 'moment';
import CustomToolbar from './CustomToolbar';
import { momentLocalizer, NavigateAction, View } from 'react-big-calendar';
import { CalendarHomeProps, CalendarSettingsProps, ViewType, CalendarEventTypeID, MAX_SELECTED_USERS_DAY_VIEW, KEYDATE_TYPES, KEYDATE_TYPES_FRONTEND, KEYDATEID_TYPE } from './Calendar.types';
import { useUsers } from '../../service/useUsers';
import {useReactToPrint } from 'react-to-print';
import PrintableCalendar from './PrintableCalendar';
import { getStartEndDateOfMonthGrid, getMinTimeSlots, getInitials, parsePrintableEvents, renderPrintDateHeader, convertTimeTo24HourFormat, createResourcesFromSelectedUsers, transformEventsForDayView } from './Calendar.helpers';
import SettingsModal from './SettingsModal';
import SearchDropdown from '../Settings/TaskManagementSettings/SearchDropdown';
import { Avatar } from "../../Components/ui/avatar/avatar";
import { TaskFuncs } from '../Settings/TaskManagementSettings/taskFuncs';
import { getCookie, setCookie } from '../topbars/topBarFuncs';
import { displayToastMsg } from '../toastMessages/toastmessages';
import AddNewAppModal from '../taskSystem/addNewApp';
import { Logger } from '../../../../share/utils/logger';

import PortalService  from '../clientportal/services/portal.service';
const localizer = momentLocalizer(moment);

const CalendarHome: FC<CalendarHomeProps> = ({ showModal, closeModal }) => {
    const [date, setDate] = useState(new Date());
    const [edgeDates, setEdgeDates] = useState(getStartEndDateOfMonthGrid(date));
    const [view, setView] = useState<View>('week');
    const [showFilters, setShowFilters] = useState(true);
    const [events, setEvents] = useState([]);
    const [filteredEvents, setFilteredEvents] = useState([]);
    const { users } = useUsers();
    const [calendarUsers, setCalendarUsers] = useState([]);

    const [filters, setFilters] = useState({
        keyDates: {
            all: true,
            requisition: true,
            closing: true,
            others: true
        },
        tasks: {
            all: true,
            onlyOverdue: false
        },
        appointments: {
            all: true
        },
        meetings: {
            all: true
        },
        undertakings: {
            all: true
        }
    });

    const [calendarSettings, setCalendarSettings] = useState<CalendarSettingsProps>({
        showTransactionType: true,
        showFileNumber: true,
        showClientName: true,
        showTaskDescription: true,
        showUserInitials: true,
        showLawyerInitials: true,
        showTransactionTypeShort: true,
        showFirmContactInitials: true,
    });

    const calendarPrintRef = useRef<HTMLDivElement>(null);
    const listPrintRef = useRef<HTMLDivElement>(null);
    const calendarPrintFn = useReactToPrint({ contentRef: calendarPrintRef });
    const listPrintFn = useReactToPrint({ contentRef: listPrintRef });
    
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
    const [minTimeSlot, setMinTimeSlot] = useState<Date | null>(null);
    const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
    const lastProcessedRef = useRef<{ userId: string; timestamp: number } | null>(null);
    const calendarAppointment = useAppSelector(state => state.CalendarAppointment);
    const prevDateRef = React.useRef(date);
    const SESSION_INFO: lddGlobalSessionInfo = useAppSelector((g) => g.SessionInfo);
    const portalService = new PortalService(SESSION_INFO);
    const loggedInUser = users.find(user => user.SubUserID.toString() === SESSION_INFO.user.id.toString());
    const taskFuncs = new TaskFuncs();
    const keydates = taskFuncs?.getProvinceTasks(SESSION_INFO.appProvinceShort);
    const requisitionTitle = keydates.find(keydate => keydate?.id === 6)?.title || '';
    let closingTitle = keydates.find(keydate => keydate?.id === 1)?.title || 'Closing';
    const province = SESSION_INFO?.appProvinceShort;
    const [showAddAppModal, setShowAddAppModal] = useState(false);
    const livedataRootContainer = useAppSelector(state => state.currentNavigation.livedataRootContainer);
    const lastSaveTime = useAppSelector((state) => state.LastSaveTime);

    if (province === 'MB') {
        closingTitle = 'Possession/Closing';
    }
    if (province === 'SK') {
        closingTitle = 'Possession/Advance';
    }

    const AVATAR_COLORS = [
        '#9E0000', '#D68400', '#408F00', '#87B45E', '#A347B6', '#5847B6', 
        '#0052AB', '#00A38C', '#006557', '#BB83AC', '#907A45', '#839ABB', 
        '#D87373', '#A45500'
    ];
    
    const getUserColor = (userId: number | string): string => {
        const numericId = typeof userId === 'string' ? userId.charCodeAt(0) : userId;
        return AVATAR_COLORS[Math.abs(numericId) % AVATAR_COLORS.length];
    };
    useEffect(() => { 
        const savedSettings = getCookie('calendarAdvancedSettings');
        if (savedSettings) {
            try {
                const parsedSettings = JSON.parse(savedSettings);
                setCalendarSettings(parsedSettings);
            } catch (error) {
                setCalendarSettings(calendarSettings);
            }
        } else {
            setCalendarSettings(calendarSettings);
        }
    }, []);
    useEffect(() => {
        if (lastSaveTime) {
            updateEvents();
        }
    }, [lastSaveTime]);
    useEffect(() => {
        if (calendarAppointment) {
            updateEvents();
        }
    }, [calendarAppointment]);
    useEffect(() => {
        if (users.length > 0 && showModal) {
            setSelectedUsers([loggedInUser?.SubUserID?.toString() ?? "Me"]);
            setCalendarUsers([loggedInUser, ...users.filter(user => user?.SubUserID?.toString() !== SESSION_INFO.user.id.toString())]);
            updateEvents();
        }
    }, [SESSION_INFO, users, showModal]);

    useEffect(() => {
        // if not same month of the year, update events
        if (prevDateRef.current.getMonth() !== date.getMonth() && prevDateRef.current.getFullYear() === date.getFullYear()) {
            updateEvents();
            prevDateRef.current = date;
        }
        prevDateRef.current = date;
    }, [date, view, edgeDates, livedataRootContainer]);

    useEffect(() => {
        updateMinTimeSlot(view, filteredEvents);
    }, [view, filteredEvents, date]);

    useEffect(() => {
        let newFilteredEvents = events.filter((event: any) => {
            if(!requisitionTitle || requisitionTitle === '') {
                filters.keyDates.requisition = true;
            }
            if (event.typeID != 100 && !event.time && !event.isMeeting) {
                if (!filters.tasks.all) {
                    return false;
                }
                if (new Date(event.start) < new Date()) {
                    if (!event.isCompleted) {
                        return (!filters.tasks.onlyOverdue && filters.tasks.all) || filters.tasks.onlyOverdue;
                    } else {
                        return !filters.tasks.onlyOverdue && filters.tasks.all;
                    }
                }
                if (new Date(event.start) > new Date()) {
                    if (event.isCompleted) {
                        return !filters.tasks.onlyOverdue && filters.tasks.all;
                    } else {
                        return filters.tasks.all;
                    }
                }
            }
            if (event.keydateType === KEYDATE_TYPES.REQUISITION) {
                return filters.keyDates.all || filters.keyDates.requisition;
            }
            if (event.keydateType === KEYDATE_TYPES.CLOSING || event.keydateType === KEYDATE_TYPES.POSSESSION || event.keydateType === KEYDATE_TYPES.ADVANCE || event.keydateId === KEYDATEID_TYPE.INTERIM_CLOSING_KEY) {
                return filters.keyDates.all || filters.keyDates.closing;
            }
            if (event.keydateType === KEYDATE_TYPES.OTHERS) {
                return filters.keyDates.all || filters.keyDates.others;
            }
            if (event.isMeeting) {
                return filters.meetings.all;
            }
            if (event.time) {
                return filters.appointments.all;
            }
            if (event.isUndertaking) {
                return filters.undertakings.all;
            }
            return false;
        });
        if (selectedUsers.length > 0) {
            newFilteredEvents = newFilteredEvents.filter(event => {
                if (event.typeID === CalendarEventTypeID.FIRM_CONTACT_EVENT || event.typeID === CalendarEventTypeID.MEETING) {
                    const firmContactUser = event.firmContact;
                    if(firmContactUser) {
                        const firmContactUserId = users.find(user => user.Name === firmContactUser)?.SubUserID;
                        if (!firmContactUserId) {
                            return selectedUsers.includes("Not Assigned");
                        }
                        return firmContactUserId && selectedUsers.includes(firmContactUserId.toString());
                    } else {
                        return selectedUsers.includes("Not Assigned");
                    }
                }

                // Handle tasks where userResponsible is not found in users array
                if (event.typeID !== CalendarEventTypeID.APPOINTMENT && event.typeID !== CalendarEventTypeID.MEETING && event.typeID !== CalendarEventTypeID.FIRM_CONTACT_EVENT && event.userResponsible) {
                    const userResponsibleIds = event.userResponsible.split(',').map(id => id.trim());

                    const foundUserIds = userResponsibleIds.filter(id => 
                        users.some(user => user.SubUserID.toString() === id)
                    );
                    
                    // If no userResponsible IDs are found in users array and "Not Assigned" is selected
                    if (foundUserIds.length === 0 && selectedUsers.includes("Not Assigned")) {
                        return true;
                    }
                    
                    // If userResponsible IDs are found, check if any selected users match
                    if (foundUserIds.length > 0) {
                        return selectedUsers.some(subUserID => foundUserIds.includes(subUserID));
                    }
                }
                
                if (selectedUsers.includes("Not Assigned") && (!event.userResponsible || event.userResponsible === "")) {
                    return true;
                }
                
                if (event.stationId && event.stationId.toLowerCase() === "defaultadmin") {
                    return selectedUsers.includes("Me");
                }
                if (event.userResponsible && !event.stationId) {
                    return selectedUsers.some(subUserID => event.userResponsible.includes(subUserID));
                }
                if (!event.userResponsible && event.stationId) {
                    const selectedUserNames = selectedUsers.map(subUserID => users.find(user => user.SubUserID.toString() === subUserID)?.SubUsername);
                    return selectedUserNames.some(userName => userName && userName.toLowerCase() === event.stationId.toLowerCase());
                }
                
                return false;
            });
        } else {
            newFilteredEvents = [];
        }

        async function updateAppts(events: any[]) {
            const appts = events.filter(event => event.time && event.apptInfo && event.apptInfo.includes('LinkToFile'));
            let apptsInfo = [];
            if (appts.length > 0) {
                for (let appt of appts) {
                    const appIdMatch = appt.apptInfo.match(/LinkToFile="([^"]+)"/);
                    const fileNameMatch = appt.apptInfo.match(/LinkToFileMatter="([^"]+)"/);
                    if (appIdMatch && fileNameMatch) {
                        apptsInfo.push({
                            appId: appIdMatch[1],
                            fileName: fileNameMatch[1]
                        });
                    }
                }
            }

            if (apptsInfo.length > 0) {
                try {
                    const queryParams = new URLSearchParams({
                        apptsInfo: JSON.stringify(apptsInfo),
                        SessionID: SESSION_INFO.sid
                    }).toString();
                    const apptRes = await axios.get(`/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${SESSION_INFO.appProvinceShort}/${SESSION_INFO.firm.id}/calendarappts?${queryParams}`);
                    if (apptRes.status === 200) {
                        const updatingEvents = [];
                        for (let appt of apptRes.data.result) {
                            for (let event of events) {
                                const apptContent = Array.isArray(appt) ? appt[0] : appt;
                                if (event.apptInfo && apptContent && event.apptInfo.includes(apptContent.appId)) {
                                    updatingEvents.push({
                                        ...event,
                                        appId: apptContent.appId,
                                        reline: apptContent.ReLine,
                                        clientName: apptContent.clientName,
                                        propertyAddress: apptContent.propertyAddress,
                                        fileName: apptContent.fileNumber,
                                        firmContact: users.find(user => apptContent.firmContactID && user.SubUserID.toString() === apptContent.firmContactID.toString())?.Name
                                    });
                                }
                            }
                        }
                        const mergedEvents = events.map(event => {
                            const updatedEvent = updatingEvents.find(updEvent => updEvent.apptInfo === event.apptInfo);
                            return updatedEvent ? updatedEvent : event;
                        });
                        setFilteredEvents(mergedEvents);
                    }
                } catch (error) {
                    console.error("Error fetching calendar appointments:", error);
                }
            } else {
                setFilteredEvents(events);
            }
        }
        if(selectedUsers.length === 0 || newFilteredEvents.length === 0) {
            setFilteredEvents([]);
        }
        if (newFilteredEvents.length > 0 && selectedUsers.length > 0) {
            updateAppts(newFilteredEvents);
        }
    }, [filters, events, selectedUsers]);

    // Create resources for day view
    const resources = view === ViewType.Day && selectedUsers.length > 0
        ? createResourcesFromSelectedUsers(selectedUsers, users, loggedInUser, getInitials, getUserColor)
        : undefined;

    // Transform events for Day view with resources (duplicate events across columns)
    const eventsForCalendar = React.useMemo(() => {
        return transformEventsForDayView(
            filteredEvents,
            view,
            resources,
            users,
            loggedInUser?.SubUserID ?? SESSION_INFO.user.id,
            selectedUsers
        );
    }, [filteredEvents, view, resources, users, loggedInUser, selectedUsers, SESSION_INFO.user.id]); 

    async function updateEvents() {
        const res = await axios.get(`/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${SESSION_INFO.appProvinceShort}/${SESSION_INFO.firm.id}/calendar?startDate=${format(edgeDates[0], 'yyyy-MM-dd')}&endDate=${format(edgeDates[1], 'yyyy-MM-dd')}&SessionID=${SESSION_INFO.sid}`);
        let meetingsRes = null;
        try {
            meetingsRes = await portalService.getPortalByLawyerId(
                SESSION_INFO.firm.id,
                SESSION_INFO.appProvinceShort,
                format(edgeDates[0], 'yyyy-MM-dd'),
                format(edgeDates[1], 'yyyy-MM-dd')
            );
        } catch (error) {
            console.error('Failed to fetch meetings:', error);
            meetingsRes = null;
        }

        let allEvents = [];
        if (res.status === 200) {
            const parsedEvents = res.data.result.map((event: any) => {
                const matchingUser = (event.userResponsible && event.userResponsible > "" )
                    ? users.find(user => event.userResponsible.split(',').includes(user.SubUserID.toString())) 
                    : null;
                const timeRegex = /^([01]?\d|2[0-3]):([0-5]?\d)$/;
                const time12HourRegex = /^(0?[1-9]|1[0-2]):([0-5]?\d)\s?(AM|PM)$/i;
                let time = undefined;
                if (event.time && event.date) {
                    if (timeRegex.test(event.time)) {
                        time = event.time;
                    } else if (time12HourRegex.test(event.time)) {
                        time = convertTimeTo24HourFormat(event.time, time12HourRegex);
                    }
                }
                if ((event.fileName && (event.typeID === CalendarEventTypeID.FIRM_CONTACT_EVENT || (event.isActivated && event.isActivated === 1))) || time) {
                    let endTime;
                    
                    if (event.time) {
                        const eventHour = moment(`${event.date} ${time}`, 'YYYY-MM-DD HH:mm').hour();
                        if (eventHour >= 23) {
                            // If event is 11 PM or later, set end time to 11:59 PM of the same day
                            endTime = moment(`${event.date} ${time}`, 'YYYY-MM-DD HH:mm').endOf('day').toDate();
                        } else {
                            // If event is before 11 PM, add 1 hour
                            endTime = moment(`${event.date} ${time}`, 'YYYY-MM-DD HH:mm').add(1, 'hour').toDate();
                        }
                    } else {
                        endTime = moment(event.date).toDate();
                    }

                    return {
                        title: event.title.trim(),
                        start: event.time ? moment(`${event.date} ${time}`, 'YYYY-MM-DD HH:mm').toDate() : moment(event.date).toDate(),
                        end: endTime,
                        calendarID: event.time ? event.id: null,
                        appId: event.appId,
                        fileName: event.fileName,
                        psm: event.PSM,
                        keydateType: event.keydateType,
                        keydateId: event.keydateId,
                        typeID: event.typeID,
                        stationId: event.stationId,
                        isKeyDate: event.keydateId !== null,
                        isCompleted: event.isCompleted === 1,
                        isOverdue: new Date(event.date) < new Date() && !event.isCompleted,
                        reline: event.ReLine,
                        propertyAddress: event.propertyAddress,
                        lawyer: event.lawyer,
                        userInitials: matchingUser ? getInitials(matchingUser.Name, matchingUser.FirstName, matchingUser.LastName) : '',
                        multipleUsers: event.userResponsible && event.userResponsible.split(',').length > 1,
                        time: time,
                        calendarItemTime:event.time? event.time : null,
                        calendarItemDate:event.date? event.date : null,
                        xmldata: event.time ? event.info : undefined,
                        apptInfo: event.time ? event.info : undefined,
                        userResponsible: event.userResponsible,
                        clientName: event.clientName,
                        avatarColor: matchingUser ? getUserColor(matchingUser.SubUserID) : '',
                        firmContact: users.find(user => event.firmContactID && user.SubUserID.toString() === event.firmContactID.toString())?.Name
                    };
                }
                return null;
            }).filter(event => event !== null)
              .sort((a, b) => {
                  if (a.isKeyDate && !b.isKeyDate) {
                      return -1;
                  }
                  if (!a.isKeyDate && b.isKeyDate) {
                      return 1;
                  }
                  return 0;
              })
              .sort((a, b) => {
                  if (a.isKeyDate && b.isKeyDate) {
                      if (a.keydateType === KEYDATE_TYPES.REQUISITION && b.keydateType !== KEYDATE_TYPES.REQUISITION) {
                          return -1;
                      }
                      if (a.keydateType === KEYDATE_TYPES.CLOSING && b.keydateType !== KEYDATE_TYPES.CLOSING) {
                          return -1;
                      }
                  }
                  return 0;
              });

            const uniqueEvents = parsedEvents.filter((event, index, self) => {
                if (!event.time) return true;
                
                const duplicates = self.filter(e => 
                    e.title === event.title && 
                    e.time === event.time && 
                    e.time !== undefined && 
                    e.time !== null && 
                    e.time !== '' &&
                    e.stationId === event.stationId
                );
                
                if (duplicates.length === 1) {
                    return true;
                }
                
                if (duplicates.length >= 2) {
                    const hasAppId = duplicates.some(dup => dup.appId !== undefined && dup.appId !== null);
                    
                    if (hasAppId) {
                        return event.appId !== undefined && event.appId !== null;
                    } else {
                        return duplicates.indexOf(event) === 0;
                    }
                }
                
                return true;
            });

            allEvents = uniqueEvents;
        }
        if (meetingsRes && meetingsRes.length > 0 && Array.isArray(meetingsRes)) {
            const uniqueAppIds = [...new Set(meetingsRes.map((m: any) => m.AppId).filter((id:any) => id))];
            
            const fileAppIdMap = new Map();
            await Promise.all(uniqueAppIds.map(async (appId) => {
                const fileData = await portalService.getFileByAppId(appId);
                if (fileData) {
                    fileAppIdMap.set(appId, fileData);
                }
            }));
            const meetings = meetingsRes.filter((valid: any) => valid.RSPScheduledStartTime && valid.RSPScheduledEndTime)
            .map((meeting: any) => {
                const startDate = new Date(meeting.RSPScheduledStartTime);
                const endDate = new Date(meeting.RSPScheduledEndTime);
                const fileInfo = fileAppIdMap.get(meeting.AppId);
                if (!fileInfo) {
                    //if change client portal detlting issue, change this to alert and return null
                    console.log('File not found for appId: ' + meeting.AppId);
                    return null;
                }else{
                return {
                    title: 'Meeting' + '--' + fileInfo.MatterNumber,
                    start: new Date(meeting.RSPScheduledStartTime),
                    end: new Date(meeting.RSPScheduledEndTime),
                    calendarID: null,
                    appId: meeting.AppId,
                    isMeeting: true,
                    typeID: 500,
                    clientName: fileInfo.ClientName,
                    propertyAddress: fileInfo.propertyAddress,
                    firmContactId: fileInfo.FirmContactID,
                    userId: fileInfo.UserID,
                    lawyerId: fileInfo.LawyerID,
                    lawyerName: fileInfo.LawyerName,
                    matterNumber: fileInfo.MatterNumber,
                    fileName: fileInfo.MatterNumber,
                    //this is for when only want to show the firm contact name in that is a user.
                    //firmContact: users.find(user => fileInfo.FirmContactID && user.SubUserID.toString() === fileInfo.FirmContactID.toString())?.Name,
                    firmContact: fileInfo.FirmContact,
                    startTime: startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                    endTime: endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                }}
            }).filter((meeting: any) => meeting !== null);
            allEvents = [...allEvents, ...meetings];

        }
        let undertakingEvents = [], undertakingRes = null;

        try{
            undertakingRes = await axios.get(`/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${SESSION_INFO.appProvinceShort}/${SESSION_INFO.firm.id}/firmundertaking?startDate=${format(edgeDates[0], 'yyyy-MM-dd')}&endDate=${format(edgeDates[1], 'yyyy-MM-dd')}&SessionID=${SESSION_INFO.sid}`)
        } catch (e) {
            console.log("error fetching undertakings")
        }
        if(undertakingRes && undertakingRes.status == 200){
            let result = undertakingRes.data.result 
            result.forEach(event => {
                let data = JSON.parse(event.CalendarData) || {};
                let FuN = event.SentDate2 > ""? 2 : event.SentDate1 > ""? 1: 0;

                let [y, m, d] = event[`Date1`]?.split("-");
                undertakingEvents.push({
                    start: new Date(y, m-1, d),
                    end: new Date(y, m-1, d),
                    psm: event.PSM || "P",
                    fileName: event.MatterNumber || "FILE NAME",
                    title: `1st Follow up ${data.title || "Unknown Issue"}`,
                    firmContact: event.FirmContact || "",
                    propertyAddress: event.PropertyAddress || "",
                    reline: event.ReLine || "",
                    isUndertaking: true,
                    typeID: 100,
                    appId: event.AppID,
                })
                if(FuN > 0){
                    let [y, m, d] = event[`Date2`]?.split("-");
                    undertakingEvents.push({
                        start: new Date(y, m-1, d),
                        end: new Date(y, m-1, d),
                        psm: event.PSM || "P",
                        fileName: event.MatterNumber || "FILE NAME",
                        title: `2nd Follow up ${data.title || "Unknown Issue"}`,
                        firmContact: event.FirmContact || "",
                        propertyAddress: event.PropertyAddress || "",
                        reline: event.ReLine || "",
                        isUndertaking: true,
                        typeID: 100,
                        appId: event.AppID,
                    })
                }
            })
            allEvents = [...allEvents, ...undertakingEvents]
        }
        setEvents(allEvents);
    }

    const updateMinTimeSlot = (view: View, events: any[]) => {
        if (view === ViewType.Week) {
            const startOfWeek = new Date(date);
            startOfWeek.setDate(date.getDate() - date.getDay());

            const endOfWeek = new Date(startOfWeek);
            endOfWeek.setDate(startOfWeek.getDate() + 6);

            const currWeekEvents = events.filter(event =>
                new Date(event.start) >= startOfWeek && new Date(event.start) <= endOfWeek
            );
            setMinTimeSlot(getMinTimeSlots(currWeekEvents));
        } else if (view === ViewType.Day) {
            setMinTimeSlot(getMinTimeSlots(events.filter(event => {
                const eventDate = new Date(event.start);
                return eventDate.getFullYear() === date.getFullYear() &&
                       eventDate.getMonth() === date.getMonth() &&
                       eventDate.getDate() === date.getDate();
            })));
        }
    }

    const handleNavigation = (action: NavigateAction) => {
        let newDate = new Date(date);
        if (action === 'TODAY') {
            newDate = new Date();
        } else {
            switch (view) {
                case ViewType.Month:
                    if (action === 'NEXT')
                        newDate.setMonth(newDate.getMonth() + 1);
                    else if (action === 'PREV')
                        newDate.setMonth(newDate.getMonth() - 1);
                    break;
                case ViewType.WorkWeek:
                case ViewType.Week:
                    if (action === 'NEXT')
                        newDate.setDate(newDate.getDate() + 7);
                    else if (action === 'PREV')
                        newDate.setDate(newDate.getDate() - 7);
                    break;
                case ViewType.Day:
                    if (action === 'NEXT')
                        newDate.setDate(newDate.getDate() + 1);
                    else if (action === 'PREV')
                        newDate.setDate(newDate.getDate() - 1);
                    break;
            }
        }
        setDate(newDate);
        if (view === ViewType.Month || newDate.getMonth() !== date.getMonth()) {
            setEdgeDates(getStartEndDateOfMonthGrid(newDate));
        }
    }

    const handleViewChange = (view: ViewType) => {
        if (view === ViewType.Day && selectedUsers.length > MAX_SELECTED_USERS_DAY_VIEW) {
            // Check if "Me" is selected (could be literal "Me" or loggedInUser's SubUserID)
            const loggedInUserId = loggedInUser?.SubUserID?.toString();
            const hasMe = loggedInUserId && selectedUsers.includes(loggedInUserId);
            const hasNotAssigned = selectedUsers.includes("Not Assigned");
            
            const otherUsers = selectedUsers.filter(
                id => id !== "Me" && id !== "Not Assigned" && id !== loggedInUserId
            );
            
            const truncatedUsers: string[] = [];
            
            if (hasNotAssigned) {
                truncatedUsers.push("Not Assigned");
            }
            
            if (hasMe) {
                truncatedUsers.push(loggedInUserId ?? "Me");
            }
            
            const remainingSlots = MAX_SELECTED_USERS_DAY_VIEW - truncatedUsers.length;
            truncatedUsers.push(...otherUsers.slice(0, remainingSlots));
            
            setSelectedUsers(truncatedUsers);
            
            setTimeout(() => {
                displayToastMsg({
                    id: null,
                    type: "info",
                    msg: `Day view supports a maximum of ${MAX_SELECTED_USERS_DAY_VIEW} users. Only the first ${MAX_SELECTED_USERS_DAY_VIEW} selected users are shown.`
                }, null, { autoClose: 5000 });
            }, 0);
        }
        setView(view);
        setEdgeDates(getStartEndDateOfMonthGrid(new Date()));
    }

    const handleDatePickerClick = (date: Date) => {
        setDate(date);
        setEdgeDates(getStartEndDateOfMonthGrid(date));
    }

    const handleKeyDatesAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        setFilters(prev => {
            const newFilters = {
                ...prev,
                keyDates: {
                    all: checked,
                    requisition: checked,
                    closing: checked,
                    others: checked
                }
            };
            setCookie('calendarViewSettings', JSON.stringify(newFilters), 365);
            return newFilters;
        });
    };

    const KEYDATE_FILTER_TYPES = [
        KEYDATE_TYPES.REQUISITION,
        KEYDATE_TYPES.CLOSING,
        KEYDATE_TYPES.OTHERS
    ] as const;
    type KeyDateFilterType = typeof KEYDATE_FILTER_TYPES[number];

    const handleKeyDateTypeChange = (type: KeyDateFilterType) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        setFilters(prev => {
            const updatedKeyDates = {
                ...prev.keyDates,
                [type]: checked
            };

            const allTypesChecked = KEYDATE_FILTER_TYPES.every(keyType =>
                keyType === type ? checked : updatedKeyDates[keyType]
            );

            const newFilters = {
                ...prev,
                keyDates: {
                    ...updatedKeyDates,
                    all: allTypesChecked
                }
            };
            setCookie('calendarViewSettings', JSON.stringify(newFilters), 365);
            return newFilters;
        });
    };

    const handleTasksAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFilters(prev => {
            const newFilters = {
                ...prev,
                tasks: {
                    all: e.target.checked,
                    onlyOverdue: prev.tasks.onlyOverdue
                }
            };
            setCookie('calendarViewSettings', JSON.stringify(newFilters), 365);
            return newFilters;
        });
    };

    const handleTasksOverdueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFilters(prev => {
            const newFilters = {
                ...prev,
                tasks: {
                    all: prev.tasks.all,
                    onlyOverdue: e.target.checked
                }
            };
            setCookie('calendarViewSettings', JSON.stringify(newFilters), 365);
            return newFilters;
        });
    };

    const handleAppointmentsAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFilters(prev => {
            const newFilters = {
                ...prev,
                appointments: {
                    all: e.target.checked
                },
                meetings: {
                    all: e.target.checked
                }
            };
            setCookie('calendarViewSettings', JSON.stringify(newFilters), 365);
            return newFilters;
        });
    };

    //if want to split meeting from appointments, add a checkbox to the filters section to show only meetings
    const handleMeetingsAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFilters(prev => {
            const newFilters = {
                ...prev,
                meetings: {
                    all: e.target.checked
                }
            };
            setCookie('calendarViewSettings', JSON.stringify(newFilters), 365);
            return newFilters;
        });
    };
    const handleUndertakingsAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFilters(prev => {
            const newFilters = {
                ...prev,
                undertakings: {
                    all: e.target.checked
                }
            };
            setCookie('calendarViewSettings', JSON.stringify(newFilters), 365);
            return newFilters;
        });
    };
    const handleUserSelect = (subUserID: string) => {
        const isAdding = !selectedUsers.includes(subUserID);
        
        // If removing, always allow
        if (!isAdding) {
            setSelectedUsers(prev => prev.filter(id => id !== subUserID));
            return;
        }
        
        // If adding and in Day view
        if (view === ViewType.Day) {
            if (selectedUsers.length === MAX_SELECTED_USERS_DAY_VIEW - 1) {
                setTimeout(() => {
                    displayToastMsg({
                        id: null,
                        type: "info",
                        msg: "You have reached the maximum of 5 users. Please deselect a user to select another."
                    }, null, { autoClose: 5000 });
                }, 0);
                
                setSelectedUsers(prev => {
                    const newUsers = [...prev, subUserID];
                    return newUsers;
                });
                return;
            }
        }
        
        // Otherwise, add the user normally (not at limit yet)
        setSelectedUsers(prev => [...prev, subUserID]);
    };

    const handleSaveSettings = (settings: CalendarSettingsProps) => {
        setCalendarSettings(settings);
    }

    const isCheckboxDisabled = (userId: string): boolean => {
        // Only disable in Day view
        if (view !== ViewType.Day) return false;
        
        // Only disable when at max limit
        if (selectedUsers.length < MAX_SELECTED_USERS_DAY_VIEW) return false;
        
        // Only disable unchecked boxes (allow unchecking to make room)
        return !selectedUsers.includes(userId);
    };

    const handleAllUsersSelect = () => {
        // Prevent "All Users" selection in Day view
        if (view === ViewType.Day) {
            return;
        }
        
        const allUserIds = users.map(user => user?.SubUserID?.toString());
        if (selectedUsers.length !== allUserIds.length + 2) { // +2 for "Me" and "Unassigned"
            setSelectedUsers([...allUserIds, "Me", "Not Assigned"]);
        } else {
            setSelectedUsers([]);
        }
    }

    const getFirstWeeklyPrintGridEvent = (date: Date) => {
        if(view === ViewType.Week || view === ViewType.WorkWeek) {
            const startOfWeek = view === ViewType.WorkWeek ? moment(date).startOf('week').add(1, 'day') : moment(date).startOf('week');
            const endOfWeek = view === ViewType.WorkWeek ? moment(date).startOf('week').add(5, 'day') : moment(date).endOf('week');
            const eventsInWeek = filteredEvents.filter(event => {
                const eventDate = moment(event.start);
                return eventDate.isBetween(startOfWeek, endOfWeek, 'day', '[]');
            }).sort((a, b) => moment(a.start).valueOf() - moment(b.start).valueOf());
            return eventsInWeek.length > 0 ? eventsInWeek[0] : null;
        }
        return null;
    }
    const handleAddAppointment = async (newAppointment: any) => {
        if (!SESSION_INFO) {
            console.error('SESSION_INFO not available');
            return;
        }

        try {
            const response = await fetch(`/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${SESSION_INFO.appProvinceShort}/${SESSION_INFO.firm.id}/set/addappointment?SessionID=${SESSION_INFO.sid}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(newAppointment)
            });

            const data = await response.json();
            
            if (!data.ok) {
                throw new Error(data.error || 'Failed to add appointment');
            }
            updateEvents();
        } catch (error) {
            Logger.error('Error adding appointment:', error);
            throw error;
        }
    };
    // Add useEffect to load saved filters on component mount
    useEffect(() => {
        const savedFilters = getCookie('calendarViewSettings');
        if (savedFilters) {
            try {
                const parsedFilters = JSON.parse(savedFilters);
                setFilters(prev => ({
                    ...prev,
                    ...parsedFilters,
                    meetings: parsedFilters.meetings || prev.meetings || { all: true } 
                }));
            } catch (error) {
                console.error('Error parsing saved filters:', error);
            }
        }
    }, []);
 
    const getVisibleRange = (view: View, date: Date, edgeDates: Date[]): [Date, Date] => {
        if (view === ViewType.Day) {
          const start = moment(date).startOf('day').toDate();
          const end = moment(date).endOf('day').toDate();
          return [start, end];
        }
      
        if (view === ViewType.Week || view === ViewType.WorkWeek) {
          const startOfWeek =
            view === ViewType.WorkWeek
              ? moment(date).startOf('week').add(1, 'day')
              : moment(date).startOf('week');
          const endOfWeek =
            view === ViewType.WorkWeek
              ? moment(date).startOf('week').add(5, 'day')
              : moment(date).endOf('week');
          return [startOfWeek.toDate(), endOfWeek.toDate()];
        }
        return [edgeDates[0], edgeDates[1]];
    };

    const filterEventsByDateRange = (events: any[], dateRange: [Date, Date]): any[] => {
        const [start, end] = dateRange;
        return events.filter(event => {
            const eventDate = moment(event.start);
            return eventDate.isBetween(moment(start), moment(end), 'day', '[]');
        });
    };

    const visibleInterimClose = React.useMemo(() => {
        const provinceCode = province?.toUpperCase();
        const dateRange = getVisibleRange(view, date, edgeDates);
        const visibleEvents = filterEventsByDateRange(eventsForCalendar, dateRange);
        const counts = visibleEvents.reduce((acc, e) => {
            if ((e.keydateType === KEYDATE_TYPES.CLOSING) || (e.keydateType === KEYDATE_TYPES_FRONTEND.POSSESSION_CORRECT) || (e.keydateType === KEYDATE_TYPES_FRONTEND.ADVANCE)) acc.closing++;
            if ((e.title === KEYDATE_TYPES_FRONTEND.INTERIM_CLOSING) || (e.title === KEYDATE_TYPES_FRONTEND.INTERIM_POSSESSION)) acc.interimClosing++; //codebase does not distinguish between 'interim closing' vs 'interim possession'. The term 'interim possession' is only used for UI display for certain provinces such as SK.
            return acc;
        }, { closing: 0, interimClosing: 0});

        if (provinceCode === 'ON' || provinceCode === 'NS' || provinceCode === 'AB') {
            return (
                <Badge pill text="dark" bg="custom">
                    {counts.interimClosing} Interim | {counts.closing} Final Closings
                </Badge>
            );
        } else if (provinceCode === 'SK') {
            return (
                <Badge pill text="dark" bg="custom">
                    {counts.interimClosing} Interim Possession | {counts.closing} Possession/Advance
                </Badge>
            );
        } else if (provinceCode === 'MB') {
            return (
                <Badge pill text="dark" bg="custom">
                   {counts.interimClosing} Interim Closing | {counts.closing} Possession/Closing
                </Badge>
            );
        } else { 
            return (
                <Badge pill text="dark" bg="custom">
                    {counts.closing} Final Closings
                </Badge>
            );
        }
    }, [view, date, edgeDates, eventsForCalendar, province]);

    return (
        <>
        <Modal show={showModal} onHide={closeModal} className="modal-calendar" backdrop={false} fullscreen={true}>
            <Modal.Header closeButton className="justify-content-start gap-3">
                <div className="d-flex align-items-center gap-5 me-0">
                        <Modal.Title className="mb-0">Calendar</Modal.Title>    
                        <Button 
                            variant="primary" 
                            onClick={() => setShowAddAppModal(true)}
                        >
                            <i className="fa-solid fa-plus me-2"></i>
                            Appointment
                        </Button>
                    </div> 
                <CustomToolbar
                    onView={handleViewChange}
                    onNavigate={handleNavigation}
                    date={date}
                    view={view}
                    onSaveSettings={(settings: any) => handleSaveSettings(settings)}
                    calendarSettings={calendarSettings}
                    printCalendar={calendarPrintFn}
                    printList={listPrintFn}
                    visibleInterimClose={visibleInterimClose} 
                />

            </Modal.Header>

            <Modal.Body className={styles.calendarContainer}>
                <div style={{ display: 'none' }}>
                    <div ref={calendarPrintRef}>
                        <h1>
                            {renderPrintDateHeader(view, getFirstWeeklyPrintGridEvent(date), date)}
                        </h1>
                        <RealtiCalendar
                            localizer={localizer}
                            currView={view}
                            events={eventsForCalendar}
                            currDate={date}
                            minTimeSlot={minTimeSlot}
                            calendarSettings={calendarSettings}
                            closeModal={closeModal}
                            resources={resources}
                            resourceAccessor={resources ? (event: any) => event.resource : undefined}
                        />
                    </div>
                    {filteredEvents.length > 0 && <div style={{ display: 'none' }}>
                        <PrintableCalendar
                            printableEvents={parsePrintableEvents(filteredEvents, view, date)}
                            ref={listPrintRef}
                            calendarSettings={calendarSettings}
                            date={date}
                            view={view} 
                        />
                    </div>}
                </div>

                {/* Left Sidebar */}
                <div className={styles.filters}>
                    <div className={styles.panel}>
                    <Calendar
                            onChange={(value) => handleDatePickerClick(value as Date)}
                            value={date}
                            className="border-0 w-100 mx-auto"
                            calendarType="US"
                            formatShortWeekday={(locale, date) => {
                                return ['S', 'M', 'T', 'W', 'T', 'F', 'S'][date.getDay()];
                            }}
                        />
                    </div>

                    {/* Filters Section - fixed header, collapsible content */}
                    <div className={styles.panel}>
                        <div
                            className="d-flex align-items-center"
                            style={{ cursor: 'pointer', justifyContent: 'space-between' }}
                        >
                            <h3>Calendar View</h3>

                            <div>
                                <i className={`fa-solid fa-gear`} onClick={() => setShowAdvancedSettings(!showAdvancedSettings)} style={{fontSize: '18px'}}></i>
                                <i className={`fa-duotone fa-circle-chevron-${showFilters ? 'up' : 'down'}`} style={{fontSize: '18px', marginLeft: '5px'}} onClick={() => setShowFilters(!showFilters)}/>
                            </div>
                        </div>

                        {showFilters && (
                            <div>
                                <Form>
                                    {/* <Form.Check 
                                        type="checkbox"
                                        id="keyDates-all"
                                        label="All Key Dates"
                                        checked={filters.keyDates.all}
                                        onChange={handleKeyDatesAllChange}
                                        className="mb-2"
                                    /> */}
                                    {requisitionTitle && requisitionTitle !== '' && 
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                        <Form.Check 
                                            type="checkbox"
                                            id="keyDates-requisition"
                                            label={requisitionTitle}
                                            checked={filters.keyDates.requisition}
                                            onChange={handleKeyDateTypeChange(KEYDATE_TYPES.REQUISITION)}
                                        />
                                        <span className="dot-indicator requisition-dot"></span>
                                    </div>}
                                   <div className="d-flex align-items-center gap-2 mb-1">
                                        <Form.Check 
                                            type="checkbox"
                                            id="keyDates-closing"
                                            label={closingTitle}
                                            checked={filters.keyDates.closing}
                                            onChange={handleKeyDateTypeChange(KEYDATE_TYPES.CLOSING)}
                                        />
                                        <span className="dot-indicator closing-dot"></span>
                                    </div>
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                        <Form.Check 
                                            type="checkbox"
                                            id="keyDates-others"
                                            label="Other Key Dates"
                                            checked={filters.keyDates.others}
                                            onChange={handleKeyDateTypeChange(KEYDATE_TYPES.OTHERS)}
                                        />
                                        <span className="dot-indicator others-dot"></span>
                                    </div>
                                    <div className="d-flex align-items-center gap-1 mb-1">
                                        <Form.Check 
                                            type="checkbox"
                                            id="tasks-all"
                                            label="Tasks"
                                            checked={filters.tasks.all}
                                            onChange={handleTasksAllChange}
                                        />
                                        <span className="dot-indicator tasks-dot"></span>
                                    </div>

                                    {filters.tasks.all && 
                                        <div className="d-flex align-items-center gap-1 mb-1 ms-3">
                                            <Form.Switch 
                                                type="switch"
                                                id="tasks-hide-completed"
                                                label="Hide Completed"
                                                checked={filters.tasks.onlyOverdue}
                                                onChange={handleTasksOverdueChange}
                                            />
                                        </div>
                                    }

                                    <div className="d-flex align-items-center gap-2">
                                        <Form.Check 
                                            type="checkbox"
                                            id="appointments-all"
                                            label="Appointments"
                                            checked={filters.appointments.all}
                                            onChange={handleAppointmentsAllChange}
                                        />
                                        <span className="dot-indicator appointment-dot"></span>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <Form.Check 
                                            type="checkbox"
                                            id="undertakings-all"
                                            label="Undertakings"
                                            checked={filters.undertakings.all}
                                            onChange={handleUndertakingsAllChange}
                                        />
                                        <span className="dot-indicator appointment-dot"></span>
                                    </div>
                                </Form>
                            </div>
                        )}
                    </div>

                    {/* Users Section - takes remaining space, scrollable */}
                    <div className={styles.panel}>
                        <h3>Users</h3>

                        <div className="mb-2">
                            <SearchDropdown
                                allValues={[
                                    ...users.map(user => ({ Name: user?.Name, Id: user?.SubUserID?.toString() })),
                                    { Name: "Not Assigned", Id: "Not Assigned" }
                                ]}
                                selectedValues={selectedUsers}
                                onValueChange={handleUserSelect}
                                hideBadge
                            />
                        </div>
                        
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <Form.Check
                                type="checkbox"
                                id="allUsersSelected"
                                checked={selectedUsers.length === users.length + 2}
                                onChange={handleAllUsersSelect}
                                disabled={view === ViewType.Day}
                                className="me-1"
                            />
                            <Avatar icon="fa-solid fa-users" size="sm" />
                            <span>All Users</span>
                        </div>

                        <div className="d-flex align-items-center gap-2 mb-2">
                            <Form.Check
                                type="checkbox"
                                id="unassigned"
                                checked={selectedUsers.includes("Not Assigned")}
                                onChange={() => handleUserSelect("Not Assigned")}
                                disabled={isCheckboxDisabled("Not Assigned")}
                                className="me-1"
                            />
                            <Avatar icon="fa-light fa-user" size="sm" invert />
                            <span>Not Assigned</span>
                        </div>
                        {calendarUsers.map(user => (
                            <div 
                                key={user?.SubUserID} 
                                className="d-flex align-items-center gap-2 mb-2"
                            >
                                <Form.Check
                                    type="checkbox"
                                    name="userSelect"
                                    id={`user-${user?.SubUserID}`}
                                    checked={selectedUsers.includes(user?.SubUserID?.toString() ?? "Me")}
                                    onChange={() => handleUserSelect(user?.SubUserID?.toString() ?? "Me")}
                                    disabled={isCheckboxDisabled(user?.SubUserID?.toString() ?? "Me")}
                                    className="me-1"
                                />
                                <Avatar initials={getInitials(user?.Name, user?.FirstName, user?.LastName)} size="sm" color={getUserColor(user?.SubUserID)} />
                                <span className="text-truncate" style={{maxWidth: '160px'}}>{ !user ? "Me" : (user?.SubUserID && user?.SubUserID === SESSION_INFO.user.id  ? "Me" : (user?.Name ?? user?.FirstName + " " + user?.LastName) )}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Main Calendar View */}
                <div className={styles.calendar}>
                    <RealtiCalendar
                        localizer={localizer}
                        currView={view}
                        events={eventsForCalendar}
                        currDate={date}
                        minTimeSlot={minTimeSlot}
                        calendarSettings={calendarSettings}
                        closeModal={closeModal}
                        resources={resources}
                        resourceAccessor={resources ? (event: any) => event.resource : undefined}
                    />
                </div>

                {showAdvancedSettings && (
                    <SettingsModal
                        isOpen={showAdvancedSettings}
                        calendarSettings={calendarSettings}
                        onClose={() => setShowAdvancedSettings(false)}
                        onSave={handleSaveSettings}
                    />
                )}
                {SESSION_INFO && (
                    <AddNewAppModal
                        showAddModal={showAddAppModal}
                        setShowAddModal={setShowAddAppModal}
                        handleAddAppointment={handleAddAppointment}
                        livedataRootContainer={livedataRootContainer}
                        SESSION_INFO={SESSION_INFO}
                        isFromCalendar={true}
                    />
                )}
            </Modal.Body>
        </Modal>
        </>
    );
};

export default CalendarHome;

